
import { Component, Prop, Vue } from 'vue-property-decorator';

import SelectField from '../../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../../framework/state';
import { getEnumerationOptions, getOptionLabel } from '../../../../../framework/service/options';
import { deleteResource, getResource, getResourcePagingNew } from '../../../../../framework/client/resource';

import { VendorContract } from '../../../../../../common/application/model/VendorContract';
import { getCustomerOptions } from '../../../../service/application_options_service';
import { formatCurrency } from '../../../../service/helper_service';
import { getVendorContracts } from '../../../../client/vendor_contract_client';
import { VendorContractType } from '../../../../../../common/application/model/enums/VendorContractType';
import { Customer } from '../../../../../../common/application/model/Customer';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';

import { FrameworkUserRole } from '../../../../../../common/framework/enumeration/FrameworkUserRole';
import { ApplicationUserRole } from '../../../../../../common/application/enumeration/ApplicationUserRole';
import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';
import { errorToast, successToast } from '../../../../service/toast_service';
import ParameterService from '../../../../service/parameter_service';

@Component({
    components: {
        SelectField,
        AutoCompleteMultipleField,
    },
})
export default class VendorContracts extends Vue {
    // Properties
    @Prop(String) readonly vehicleContractId!: string;
    @Prop(String) readonly customerId!: string;
    @Prop(String) readonly mode!: string;

    readonly resourceType = ApplicationResource.VENDOR_CONTRACT;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as VendorContract[],
        customer: undefined as Customer | undefined,
        total: 0,
        loading: false,
        selected: undefined as VendorContract | undefined,
        customerOptions: [] as { id: string | undefined; label: string }[],
        vendorContractTypeOptions: [] as { id: string | undefined; label: string }[],
        parameters: this.parameterService.get(),
    };

    async mounted() {
        this.local.customerOptions = await getCustomerOptions();
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
        await this.loadAsyncData();

        this.local.loading = false;
    }

    async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();
        if (this.customerId) {
            backendParameters.set('customerId', [this.customerId]);
            this.local.customer = await getResource<Customer>(ApplicationResource.CUSTOMER, this.customerId);
        }

        if (this.vehicleContractId) {
            backendParameters.set('vehicleContractId', [this.vehicleContractId]);
        }

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;

        this.local.rows = [];

        const rows = await getVendorContracts(backendParameters);

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.checkForTrackingContract();

        this.local.loading = false;
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    getCustomerName(id: string): string {
        return getOptionLabel(id, this.local.customerOptions);
    }

    getVendorContractType(id: string): string {
        return getOptionLabel(id, this.local.vendorContractTypeOptions);
    }

    add() {
        this.$router.push({ path: `/${this.resourceType}/add`, query: { vehicleContractId: this.vehicleContractId } });
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    view() {
        if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}`);
        }
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    onRowClick(row: VendorContract) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    private checkForTrackingContract() {
        for (const row of this.local.rows) {
            if (row.vendorContractType === VendorContractType.VEHICLE_TRACKING) {
                this.$emit('hasVehicleTracking');
                break;
            }
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    autoCompleteParameters(): Map<string, any> {
        const parameters = new Map<string, any>();

        if (this.local.parameters.customerId) {
            parameters.set('customerId', this.local.parameters.customerId);
        }

        return parameters;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
